import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import useBrokerId from '../../../../common/src/hooks/query-params/useBrokerId';
import { useFlowsDispatch } from '../../../../common/src/utils/globalVariables';
import { getUseConversationQKey } from '../../../../conversations/src/features/detail/shared/api/getConversationById';
import { saveStore } from '../../store/context/FlowsStorageProvider';
import { setIsSessionActive } from '../../store/redux/slices/flowsMetaDataSlice';
import {
  setIsLoading,
  setViewOnlyMode,
} from '../../store/redux/slices/flowsSlice';

interface UseFlowsInitCycle {
  startFlowInit: (flowId: string, viewOnly?: boolean) => void;
  finishFlowInit: (sessionId: string) => Promise<void>;
}

/**
 * This hook contains lifecycle hook methods
 * This should be used for transitioning as follows:
 * - INACTIVE -> LOADING -> ACTIVE
 * - startFlowInit will put the flow on LOADING
 * - finishFlowInit will put the flow on ACTIVE
 */
const useFlowsInitCycle = (): UseFlowsInitCycle => {
  const queryClient = useQueryClient();
  const flowsDispatch = useFlowsDispatch();
  const history = useHistory();
  const qBrokerId = useBrokerId();

  const startFlowInit = (flowId: string, viewOnly?: boolean) => {
    flowsDispatch(setIsLoading(true));
    // This route will show the loading state
    history.push({
      pathname: `/flows/${flowId}`,
      search: qBrokerId ? `?broker_id=${qBrokerId}` : undefined,
    });
    if (viewOnly) flowsDispatch(setViewOnlyMode(viewOnly));
  };

  /** Make sure this is called before any routes are pushed */
  const finishFlowInit = async (sessionId: string) => {
    queryClient.removeQueries(getUseConversationQKey(sessionId));

    flowsDispatch(setIsSessionActive(false));
    flowsDispatch(setIsLoading(false));

    await saveStore(sessionId);
  };

  return {
    startFlowInit,
    finishFlowInit,
  };
};

export default useFlowsInitCycle;
